// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared hook for bills cache management: auto-polling and stale cache handling.
 *
 * Used by both MarylandBillsBrowser and FederalBillsBrowser.
 */
import { useEffect, useRef, useState } from 'react'
import type { NodeApiClient } from '@/app-shell/services'

export interface BillsFetchResult<T> {
  bills: T[]
  cacheStatus: string | undefined
}

export interface UseBillsCacheOptions<T> {
  apiClient: NodeApiClient
  /** Datasource identifier (e.g. 'maryland', 'federal') */
  datasource: string
  /** Session string (e.g. '2024RS', '119') */
  session: string | null
  /** Function to fetch bills for the current session */
  fetchBills: () => Promise<BillsFetchResult<T>>
  onLoadStart?: () => void
  onLoadEnd?: () => void
}

export interface UseBillsCacheResult<T> {
  bills: T[]
  setBills: React.Dispatch<React.SetStateAction<T[]>>
  loading: boolean
  error: string | null
  cacheStatus: string | null
}

export function useBillsCache<T>({
  session,
  fetchBills,
  onLoadStart,
  onLoadEnd,
}: UseBillsCacheOptions<T>): UseBillsCacheResult<T> {
  const [bills, setBills] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cacheStatus, setCacheStatus] = useState<string | null>(null)

  // Auto-poll ref for cleanup
  const autoPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch bills when session changes
  useEffect(() => {
    if (session === null) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setCacheStatus(null)
    onLoadStart?.()

    fetchBills()
      .then((response) => {
        if (cancelled) return
        setBills(response.bills)
        setCacheStatus(response.cacheStatus ?? null)

        // Auto-poll when cache is still building
        if (response.cacheStatus === 'refreshing' && response.bills.length === 0) {
          autoPollRef.current = setInterval(async () => {
            try {
              const retry = await fetchBills()
              if (cancelled) { clearInterval(autoPollRef.current!); return }
              if (retry.cacheStatus !== 'refreshing' || retry.bills.length > 0) {
                setBills(retry.bills)
                setCacheStatus(retry.cacheStatus ?? 'ready')
                setLoading(false)
                clearInterval(autoPollRef.current!)
                autoPollRef.current = null
              }
            } catch {
              if (autoPollRef.current) clearInterval(autoPollRef.current)
              autoPollRef.current = null
              if (!cancelled) setLoading(false)
            }
          }, 10000)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err))
          setBills([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
          onLoadEnd?.()
        }
      })

    return () => {
      cancelled = true
      if (autoPollRef.current) {
        clearInterval(autoPollRef.current)
        autoPollRef.current = null
      }
    }
  }, [session])

  return {
    bills,
    setBills,
    loading,
    error,
    cacheStatus,
  }
}
